import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";

export function EditUserDialog({ user, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (user) setForm({
      name: user.name || "", username: user.username || "", email: user.email || "",
      phone: user.phone || "", bio: user.bio || "", seller_type: user.seller_type || "Individual",
      verified_seller: !!(user.verified_seller || (user.is_verified_seller && user.seller_type === "Individual")),
      verified_business: !!(user.verified_business || (user.is_verified_seller && user.seller_type !== "Individual")),
      never_expires_seller: !!(user.verified_seller && !(user.verified_seller_expires || "")),
      never_expires_business: !!(user.verified_business && !(user.verified_business_expires || "")),
      verified_breeder: !!user.verified_breeder,
      never_expires_breeder: !!(user.verified_breeder && !(user.verified_breeder_expires || "")),
    });
  }, [user]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.user_id}`, form);
      toast.success("Profile updated");
      onSaved();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto bg-white" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A]">Edit user profile</DialogTitle>
          <DialogDescription className="text-slate-500">Full control over this user's account details.</DialogDescription>
        </DialogHeader>
        {user && (
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-user-name" className="mt-1 h-10 rounded-xl" /></div>
            <div><Label>Username</Label><Input value={form.username} onChange={(e) => set("username", e.target.value)} data-testid="edit-user-username" className="mt-1 h-10 rounded-xl" placeholder="public handle" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="edit-user-email" className="mt-1 h-10 rounded-xl" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="edit-user-phone" className="mt-1 h-10 rounded-xl" /></div>
            <div>
              <Label>Seller type</Label>
              <Select value={form.seller_type} onValueChange={(v) => set("seller_type", v)}>
                <SelectTrigger data-testid="edit-user-seller-type" className="mt-1 h-10 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {["Individual", "Breeder", "Business", "Shelter"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Bio</Label><Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="edit-user-bio" rows={2} className="mt-1 rounded-xl" /></div>
            <div className="flex items-center justify-between py-1">
              <Label>Verified seller</Label>
              <Switch checked={form.verified_seller} onCheckedChange={(v) => set("verified_seller", v)} data-testid="edit-user-verified-seller" />
            </div>
            {form.verified_seller && (
              <div className="flex items-center justify-between py-1 pl-4">
                <Label className="text-slate-500 text-sm">↳ Never expires</Label>
                <Switch checked={form.never_expires_seller} onCheckedChange={(v) => set("never_expires_seller", v)} data-testid="edit-user-never-seller" />
              </div>
            )}
            <div className="flex items-center justify-between py-1">
              <Label>Verified business</Label>
              <Switch checked={form.verified_business} onCheckedChange={(v) => set("verified_business", v)} data-testid="edit-user-verified-business" />
            </div>
            {form.verified_business && (
              <div className="flex items-center justify-between py-1 pl-4">
                <Label className="text-slate-500 text-sm">↳ Never expires</Label>
                <Switch checked={form.never_expires_business} onCheckedChange={(v) => set("never_expires_business", v)} data-testid="edit-user-never-business" />
              </div>
            )}
            <div className="flex items-center justify-between py-1">
              <Label className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-purple-600" /> Verified breeder</Label>
              <Switch checked={form.verified_breeder} onCheckedChange={(v) => set("verified_breeder", v)} data-testid="edit-user-verified-breeder" />
            </div>
            {form.verified_breeder && (
              <div className="flex items-center justify-between py-1 pl-4">
                <Label className="text-slate-500 text-sm">↳ Never expires</Label>
                <Switch checked={form.never_expires_breeder} onCheckedChange={(v) => set("never_expires_breeder", v)} data-testid="edit-user-never-breeder" />
              </div>
            )}
            <Button onClick={save} disabled={saving} data-testid="save-user-btn" className="w-full h-11 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">Save changes</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
